<script lang="ts" setup>
import type { FormEntry, FormType } from '@forms/blueprints/form';
import type { AttachmentsContract } from '@forms/blueprints/attachments';
import { instanceOfAttachmentsEntry } from '@forms/blueprints/attachments';
import type { FormBuilderContract } from '@builder/form';
import type { Entry } from '@builder/form/entries/Entry';
import type { Blueprint } from '@builder/base/blueprints/Blueprint';
import FormsService from '../../runtime/services/FormsService';
import { FluentFormBuilder } from '.';
import StorageService from '#layers/core/app/runtime/services/StorageService';

const document = defineModel<FormEntry>('document');
const props = withDefaults(defineProps<{
    disabled?: boolean;
    categoryId?: number;
    formId?: number;
    published?: boolean;
    unavailable?: string[];
}>(), {
    disabled: false,
    categoryId: null,
    formId: null,
    published: true,
    unavailable: (): string[] => []
});

const { $alert } = useAlerts();

const loading = ref<boolean>(false);
const builder = ref(null) as Ref<FormBuilderContract>;
const definition = ref(null) as Ref<FormType>;

function getBuilder()
{
    return builder.value;
}

function getDefinition()
{
    return definition.value;
}

function validate()
{
    return builder.value.validateEntry();
};

async function collect(container: string, payload: Record<string, any>)
{
    return await builder.value.collectEntry(async (blueprint: Blueprint, entry: Entry) =>
    {
        if (instanceOfAttachmentsEntry(entry) && entry.data)
        {
            const attachmentsBlueprint = blueprint as AttachmentsContract;

            await entry.upload(attachmentsBlueprint, async (file, index) =>
            {
                return await StorageService.upload(file, container, {
                    ...payload,
                    categoryId: props.categoryId,
                    formId: props.formId,
                    field: blueprint.name
                }, false, !!attachmentsBlueprint.convertTo, attachmentsBlueprint.optimizeImage);
            });
        }
    });
};

onMounted(async () =>
{
    loading.value = true;

    try
    {
        const result = props.published
            ? await FormsService.fetchPublished(props.formId)
            : await FormsService.fetchDefinition(props.formId);

        builder.value = FluentFormBuilder
            .setup(false, true)
            .blueprint(() => result.definition, () => props.formId)
            .entry(() => document.value, () => 0)
            .make();
        builder.value.schema.components(props.unavailable).forEach(c => c.disabled = true);
        definition.value = builder.value.getBlueprint() as FormType;
        document.value = builder.value.getEntry() as FormEntry;
    }
    catch (ex: any)
    {
        if (ex.code == 400)
            $alert.warning(ex.message);
    }
    finally
    {
        loading.value = false;
    }
});

defineExpose({
    validate,
    collect,
    getBuilder,
    getDefinition
});
</script>

<template>
    <FormBlueprint
        v-if="!loading && definition"
        :builder="builder"
        :disabled="disabled"
    />
</template>
